import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { CalendarIcon, EyeIcon, XIcon, AlertCircleIcon, ChevronDownIcon, CheckCircleIcon, AlertTriangleIcon, CreditCardIcon, WalletIcon, RepeatIcon } from 'lucide-react';
import { mockBills, scheduleBills } from '../utils/mockBills';
import { mockSuppliers } from '../utils/mockSuppliers';
type ScheduleType = 'due_date' | 'today' | 'next_cycle';
interface BillRowState {
  billId: string;
  paymentMethod: string;
  designatedApprover: string;
  hasError: boolean;
}
const APPROVERS = [{
  value: 'auto',
  label: 'Assign Automatically'
}, {
  value: 'Simon Verraest',
  label: 'Simon Verraest'
}, {
  value: 'John Smith',
  label: 'John Smith'
}, {
  value: 'Sarah Johnson',
  label: 'Sarah Johnson'
}];
function getSupplierPaymentMethod(supplierId: string) {
  const supplier = mockSuppliers.find(s => s.id === supplierId);
  if (!supplier) return {
    method: '',
    label: 'No payment method',
    options: []
  };
  if (supplier.payInMethod === 'card' && supplier.cardNumber) {
    const last4 = supplier.cardNumber.slice(-4);
    const brand = supplier.cardNumber.startsWith('4') ? 'Visa' : 'Mastercard';
    return {
      method: 'card',
      label: `${brand} ending in ${last4}`,
      options: [{
        value: `card_${supplierId}`,
        label: `${brand} ending in ${last4}`
      }, {
        value: 'acquiring',
        label: 'Acquiring Balance'
      }]
    };
  }
  return {
    method: 'acquiring',
    label: 'Acquiring Balance',
    options: [{
      value: 'acquiring',
      label: 'Acquiring Balance'
    }]
  };
}
function getNextCycleDate(cycleDays: number[]): {
  date: string;
  label: string;
} | null {
  if (cycleDays.length === 0) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const currentDay = today.getDate();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();
  let nextDate: Date | null = null;
  for (const day of cycleDays) {
    let candidate: Date;
    if (day === -1) {
      // Last day of current month
      const lastDayThisMonth = new Date(currentYear, currentMonth + 1, 0);
      if (lastDayThisMonth > today) {
        candidate = lastDayThisMonth;
      } else {
        // Last day of next month
        candidate = new Date(currentYear, currentMonth + 2, 0);
      }
    } else {
      // Specific day of month
      candidate = new Date(currentYear, currentMonth, day);
      if (candidate <= today) {
        // Move to next month
        candidate = new Date(currentYear, currentMonth + 1, day);
      }
    }
    if (!nextDate || candidate < nextDate) {
      nextDate = candidate;
    }
  }
  if (!nextDate) return null;
  return {
    date: nextDate.toISOString().split('T')[0],
    label: nextDate.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    })
  };
}
function getPaymentDate(scheduleType: ScheduleType, dueDate: string, cycleDays?: number[]): string {
  if (scheduleType === 'today') {
    return new Date().toISOString().split('T')[0];
  }
  if (scheduleType === 'next_cycle' && cycleDays) {
    const nextCycle = getNextCycleDate(cycleDays);
    if (nextCycle) return nextCycle.date;
    // Fallback to today if no cycles configured
    return new Date().toISOString().split('T')[0];
  }
  // due_date: use the bill's due date, but if it's in the past, use today
  const due = new Date(dueDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (due < today) {
    return today.toISOString().split('T')[0];
  }
  return dueDate;
}
function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
}
export function BillApprovalWizard() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [searchParams] = useSearchParams();
  const idsParam = searchParams.get('ids');
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [scheduleType, setScheduleType] = useState<ScheduleType>('due_date');
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  // Read payment cycles from localStorage
  const paymentCycles = useMemo(() => {
    const stored = localStorage.getItem('billPaymentCycles');
    return stored ? JSON.parse(stored) as number[] : [];
  }, []);
  const nextCycleInfo = useMemo(() => getNextCycleDate(paymentCycles), [paymentCycles]);
  // Get selected bills
  const selectedBills = useMemo(() => {
    if (id && id !== 'schedule') {
      return mockBills.filter(b => b.id === id);
    }
    if (idsParam) {
      const ids = idsParam.split(',');
      return mockBills.filter(b => ids.includes(b.id));
    }
    return [];
  }, [id, idsParam]);
  // Per-bill row state
  const [billRows, setBillRows] = useState<BillRowState[]>([]);
  // Initialize bill row state from supplier data
  useEffect(() => {
    if (selectedBills.length > 0 && billRows.length === 0) {
      const rows = selectedBills.map(bill => {
        const supplierPay = getSupplierPaymentMethod(bill.supplierId);
        const defaultMethod = supplierPay.options.length > 0 ? supplierPay.options[0].value : '';
        return {
          billId: bill.id,
          paymentMethod: defaultMethod,
          designatedApprover: 'auto',
          hasError: !defaultMethod
        };
      });
      setBillRows(rows);
    }
  }, [selectedBills]);
  const totalAmount = selectedBills.reduce((sum, b) => sum + b.amount, 0);
  // Validation
  const allValid = billRows.every(r => r.paymentMethod !== '');
  const invalidCount = billRows.filter(r => r.paymentMethod === '').length;
  // Redirect if no bills selected
  useEffect(() => {
    if (selectedBills.length === 0) {
      navigate('/bills');
    }
  }, [selectedBills, navigate]);
  const updateBillRow = (billId: string, updates: Partial<BillRowState>) => {
    setBillRows(prev => prev.map(r => {
      if (r.billId !== billId) return r;
      const updated = {
        ...r,
        ...updates
      };
      updated.hasError = !updated.paymentMethod;
      return updated;
    }));
  };
  const handleConfirmSchedule = () => {
    if (!allValid) {
      setShowValidationErrors(true);
      return;
    }
    const assignments: Record<string, {
      approver: string;
      paymentMethod: string;
      paymentDate: string;
    }> = {};
    billRows.forEach(row => {
      const bill = selectedBills.find(b => b.id === row.billId);
      if (bill) {
        assignments[row.billId] = {
          approver: row.designatedApprover,
          paymentMethod: row.paymentMethod,
          paymentDate: getPaymentDate(scheduleType, bill.dueDate, paymentCycles)
        };
      }
    });
    scheduleBills(billRows.map(r => r.billId), assignments);
    setStep(3);
  };
  // Compute which bills are assigned to "me" (Simon Verraest)
  const assignedToMe = billRows.filter(r => r.designatedApprover === 'Simon Verraest');
  const autoAssigned = billRows.filter(r => r.designatedApprover === 'auto');
  if (selectedBills.length === 0) return null;
  return <div className="fixed inset-0 z-50 bg-[#F8F9FA] overflow-y-auto" data-id="element-4995">
      {/* Top Navigation Bar */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between sticky top-0 z-10" data-id="element-4996">
        <button onClick={() => navigate('/bills')} className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors" data-id="element-4997">
          <XIcon size={20} data-id="element-4998" />
        </button>
        {/* Progress Bar */}
        <div className="flex items-center gap-3" data-id="element-4999">
          <span className="text-xs text-gray-500 font-medium" data-id="element-5000">
            Step {step} of 3
          </span>
          <div className="w-64 h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-5001">
            <div className="h-full bg-gradient-to-r from-[#F7941D] to-[#F9A826] transition-all duration-300 ease-in-out rounded-full" style={{
            width: step === 1 ? '33%' : step === 2 ? '66%' : '100%'
          }} data-id="element-5002" />
          </div>
        </div>
        <div className="w-10" data-id="element-5003" />
      </div>

      <div className="max-w-6xl mx-auto py-10 px-4 sm:px-6" data-id="element-5004">
        {/* ========== STEP 1: Schedule Selection ========== */}
        {step === 1 && <div className="animate-in fade-in slide-in-from-bottom-4 duration-300 max-w-3xl mx-auto" data-id="element-5005">
            <div className="text-center mb-10" data-id="element-5006">
              <div className="w-12 h-12 bg-white rounded-full shadow-sm border border-gray-100 flex items-center justify-center mx-auto mb-6" data-id="element-5007">
                <CalendarIcon size={24} className="text-gray-900" data-id="element-5008" />
              </div>
              <h1 className="text-2xl font-semibold text-gray-900 mb-2" data-id="element-5009">
                When would you like the payments to be scheduled?
              </h1>
              <p className="text-sm text-gray-500" data-id="element-5010">
                Select one of the options below:
              </p>
            </div>

            <div className="space-y-4 mb-8" data-id="element-5011">
              <label className={`block bg-white border rounded-xl p-6 cursor-pointer transition-all ${scheduleType === 'due_date' ? 'border-[#F7941D] ring-1 ring-[#F7941D] shadow-sm' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-5012">
                <div className="flex items-start" data-id="element-5013">
                  <div className="flex items-center h-5" data-id="element-5014">
                    <input type="radio" name="scheduleType" value="due_date" checked={scheduleType === 'due_date'} onChange={() => setScheduleType('due_date')} className="w-4 h-4 text-[#F7941D] border-gray-300 focus:ring-[#F7941D] mt-0.5" data-id="element-5015" />
                  </div>
                  <div className="ml-4" data-id="element-5016">
                    <span className="block text-sm font-semibold text-gray-900 mb-1" data-id="element-5017">
                      Payment scheduled on invoice due date
                    </span>
                    <span className="block text-xs text-gray-500 leading-relaxed" data-id="element-5018">
                      The payments for the selected bills will have their payout
                      date set to the due date specified in the bill. If the
                      bill due date has passed, the payout date will be set to
                      immediately after they are approved.
                    </span>
                  </div>
                </div>
              </label>

              <label className={`block bg-white border rounded-xl p-6 cursor-pointer transition-all ${scheduleType === 'today' ? 'border-[#F7941D] ring-1 ring-[#F7941D] shadow-sm' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-5019">
                <div className="flex items-start" data-id="element-5020">
                  <div className="flex items-center h-5" data-id="element-5021">
                    <input type="radio" name="scheduleType" value="today" checked={scheduleType === 'today'} onChange={() => setScheduleType('today')} className="w-4 h-4 text-[#F7941D] border-gray-300 focus:ring-[#F7941D] mt-0.5" data-id="element-5022" />
                  </div>
                  <div className="ml-4" data-id="element-5023">
                    <span className="block text-sm font-semibold text-gray-900 mb-1" data-id="element-5024">
                      Payment scheduled today
                    </span>
                    <span className="block text-xs text-gray-500 leading-relaxed" data-id="element-5025">
                      The payments for the selected bills will be paid out
                      immediately after they are approved regardless of their
                      billing due date.
                    </span>
                  </div>
                </div>
              </label>

              {paymentCycles.length > 0 && <label className={`block bg-white border rounded-xl p-6 cursor-pointer transition-all ${scheduleType === 'next_cycle' ? 'border-[#F7941D] ring-1 ring-[#F7941D] shadow-sm' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-5026">
                  <div className="flex items-start" data-id="element-5027">
                    <div className="flex items-center h-5" data-id="element-5028">
                      <input type="radio" name="scheduleType" value="next_cycle" checked={scheduleType === 'next_cycle'} onChange={() => setScheduleType('next_cycle')} className="w-4 h-4 text-[#F7941D] border-gray-300 focus:ring-[#F7941D] mt-0.5" data-id="element-5029" />
                    </div>
                    <div className="ml-4" data-id="element-5030">
                      <span className="block text-sm font-semibold text-gray-900 mb-1" data-id="element-5031">
                        Payment scheduled on next cycle
                      </span>
                      <span className="block text-xs text-gray-500 leading-relaxed" data-id="element-5032">
                        The payments for the selected bills will be scheduled
                        for the next cycle based on your defined payment cycle.
                        This is useful for recurring bills.
                      </span>
                      {nextCycleInfo && <div className="mt-2 p-2 bg-[#F0F2F5] rounded text-xs text-gray-600" data-id="element-5033">
                          Next cycle: {nextCycleInfo.label}
                        </div>}
                    </div>
                  </div>
                </label>}
            </div>

            <div className="text-center mb-10" data-id="element-5034">
              <p className="text-xs text-gray-500" data-id="element-5035">
                Define your payment cycle to align with your billing cycle.{' '}
                <button className="text-[#F7941D] font-medium hover:underline" data-id="element-5036">
                  Go to bills settings.
                </button>
              </p>
            </div>

            <div className="flex justify-center gap-4" data-id="element-5037">
              <button onClick={() => navigate('/bills')} className="px-8 py-2.5 text-sm font-medium text-[#F7941D] bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-id="element-5038">
                Cancel
              </button>
              <button onClick={() => setStep(2)} className="px-8 py-2.5 text-sm font-medium text-white bg-[#F7941D] rounded-lg hover:bg-[#D97B06] transition-colors shadow-sm" data-id="element-5039">
                Next
              </button>
            </div>
          </div>}

        {/* ========== STEP 2: Review & Confirm ========== */}
        {step === 2 && <div className="animate-in fade-in slide-in-from-right-8 duration-300" data-id="element-5040">
            <div className="text-center mb-8" data-id="element-5041">
              <div className="w-12 h-12 bg-white rounded-full shadow-sm border border-gray-100 flex items-center justify-center mx-auto mb-6" data-id="element-5042">
                <EyeIcon size={24} className="text-gray-900" data-id="element-5043" />
              </div>
              <h1 className="text-2xl font-semibold text-gray-900 mb-2" data-id="element-5044">
                Review and confirm
              </h1>
              <p className="text-sm text-gray-500" data-id="element-5045">
                Please review the details carefully before you proceed.
              </p>
            </div>

            {/* Info Banner */}
            <div className="bg-[#F0F2F5] rounded-xl p-5 mb-6 flex items-start" data-id="element-5046">
              <AlertCircleIcon size={20} className="text-gray-600 mr-3 flex-shrink-0 mt-0.5" data-id="element-5047" />
              <div data-id="element-5048">
                <h3 className="text-sm font-semibold text-gray-900 mb-1" data-id="element-5049">
                  How to approve bill payment schedules
                </h3>
                <p className="text-xs text-gray-600 leading-relaxed" data-id="element-5050">
                  After submitting the bill payment schedules, they need to be
                  approved by authorised users. Users with appropriate access
                  levels will be able to approve bills. If you wish to assign
                  bills to specific users instead you can assign them to a
                  specific bill. Note that if you continue with automatic
                  assignment specific users might still be assigned to a bill
                  depending on the billing amount thresholds configured under
                  Bill Settings.
                </p>
              </div>
            </div>

            {/* Summary Card */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-6" data-id="element-5051">
              <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50" data-id="element-5052">
                <h3 className="text-sm font-semibold text-gray-900" data-id="element-5053">Summary</h3>
              </div>
              <div className="p-6" data-id="element-5054">
                <div className="grid grid-cols-3 gap-6" data-id="element-5055">
                  <div data-id="element-5056">
                    <span className="text-xs text-gray-500 block mb-1" data-id="element-5057">
                      Total payments
                    </span>
                    <span className="text-lg font-semibold text-gray-900" data-id="element-5058">
                      {selectedBills.length}
                    </span>
                  </div>
                  <div data-id="element-5059">
                    <span className="text-xs text-gray-500 block mb-1" data-id="element-5060">
                      Total amount
                    </span>
                    <span className="text-lg font-semibold text-gray-900" data-id="element-5061">
                      THB{' '}
                      {totalAmount.toLocaleString('en-US', {
                    minimumFractionDigits: 2
                  })}
                    </span>
                  </div>
                  <div data-id="element-5062">
                    <span className="text-xs text-gray-500 block mb-1" data-id="element-5063">
                      Payment schedule
                    </span>
                    <span className="text-lg font-semibold text-gray-900" data-id="element-5064">
                      {scheduleType === 'due_date' ? 'On due date' : scheduleType === 'today' ? 'Immediately' : 'Next cycle'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Payments Table */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-6" data-id="element-5065">
              <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between" data-id="element-5066">
                <h3 className="text-sm font-semibold text-gray-900" data-id="element-5067">
                  Payments to be scheduled
                </h3>
                {showValidationErrors && invalidCount > 0 && <span className="text-xs text-red-500 font-medium flex items-center gap-1" data-id="element-5068">
                    <AlertTriangleIcon size={12} data-id="element-5069" />
                    {invalidCount} bill{invalidCount !== 1 ? 's' : ''} missing
                    payment method
                  </span>}
              </div>
              <div className="overflow-x-auto" data-id="element-5070">
                <table className="w-full text-left" data-id="element-5071">
                  <thead data-id="element-5072">
                    <tr className="border-b border-gray-100" data-id="element-5073">
                      <th className="px-5 py-3 text-xs font-medium text-gray-500 whitespace-nowrap" data-id="element-5074">
                        Bill Details
                      </th>
                      <th className="px-5 py-3 text-xs font-medium text-gray-500 whitespace-nowrap" data-id="element-5075">
                        Bill Due Date
                      </th>
                      <th className="px-5 py-3 text-xs font-medium text-gray-500 whitespace-nowrap" data-id="element-5076">
                        Payment Date
                      </th>
                      <th className="px-5 py-3 text-xs font-medium text-gray-500 whitespace-nowrap text-right" data-id="element-5077">
                        Amount
                      </th>
                      <th className="px-5 py-3 text-xs font-medium text-gray-500 whitespace-nowrap" style={{
                    minWidth: 200
                  }} data-id="element-5078">
                        Pay bill with
                      </th>
                      <th className="px-5 py-3 text-xs font-medium text-gray-500 whitespace-nowrap" style={{
                    minWidth: 200
                  }} data-id="element-5079">
                        Designated approver
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100" data-id="element-5080">
                    {selectedBills.map(bill => {
                  const rowState = billRows.find(r => r.billId === bill.id);
                  const supplierPay = getSupplierPaymentMethod(bill.supplierId);
                  const paymentDate = getPaymentDate(scheduleType, bill.dueDate);
                  const isPastDue = scheduleType === 'due_date' && new Date(bill.dueDate) < new Date();
                  const hasError = showValidationErrors && rowState?.paymentMethod === '';
                  return <tr key={bill.id} className={hasError ? 'bg-red-50/40' : ''} data-id="element-5081">
                          <td className="px-5 py-4" data-id="element-5082">
                            <div className="text-sm font-semibold text-gray-900" data-id="element-5083">
                              {bill.supplierName}
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5" data-id="element-5084">
                              {bill.billNumber}
                            </div>
                          </td>
                          <td className="px-5 py-4 text-sm text-gray-900 whitespace-nowrap" data-id="element-5085">
                            {formatDate(bill.dueDate)}
                          </td>
                          <td className="px-5 py-4 whitespace-nowrap" data-id="element-5086">
                            <div className="flex items-center gap-1.5" data-id="element-5087">
                              <span className="text-sm font-medium text-gray-900" data-id="element-5088">
                                {formatDate(paymentDate)}
                              </span>
                              {isPastDue && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700" data-id="element-5089">
                                  Adjusted
                                </span>}
                              {scheduleType === 'today' && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700" data-id="element-5090">
                                  Immediate
                                </span>}
                            </div>
                          </td>
                          <td className="px-5 py-4 text-right whitespace-nowrap" data-id="element-5091">
                            <span className="text-sm font-semibold text-gray-900" data-id="element-5092">
                              {bill.currency}{' '}
                              {bill.amount.toLocaleString('en-US', {
                          minimumFractionDigits: 2
                        })}
                            </span>
                          </td>
                          <td className="px-5 py-4" data-id="element-5093">
                            <div className="relative" data-id="element-5094">
                              <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-5095">
                                {rowState?.paymentMethod?.startsWith('card') ? <CreditCardIcon size={14} className="text-gray-400" data-id="element-5096" /> : <WalletIcon size={14} className="text-gray-400" data-id="element-5097" />}
                              </div>
                              <select value={rowState?.paymentMethod || ''} onChange={e => updateBillRow(bill.id, {
                          paymentMethod: e.target.value
                        })} className={`w-full appearance-none pl-8 pr-8 py-2 text-sm border rounded-lg focus:ring-1 focus:ring-[#F7941D] focus:border-[#F7941D] bg-white ${hasError ? 'border-red-300 text-red-600' : 'border-gray-200 text-gray-900'}`} data-id="element-5098">
                                {supplierPay.options.length === 0 && <option value="" data-id="element-5099">
                                    No payment method configured
                                  </option>}
                                {supplierPay.options.map(opt => <option key={opt.value} value={opt.value} data-id="element-5100">
                                    {opt.label}
                                  </option>)}
                              </select>
                              <ChevronDownIcon size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-5101" />
                            </div>
                            {hasError && <p className="text-[10px] text-red-500 mt-1" data-id="element-5102">
                                Payment method required
                              </p>}
                          </td>
                          <td className="px-5 py-4" data-id="element-5103">
                            <div className="relative" data-id="element-5104">
                              <select value={rowState?.designatedApprover || 'auto'} onChange={e => updateBillRow(bill.id, {
                          designatedApprover: e.target.value
                        })} className="w-full appearance-none px-3 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-[#F7941D] focus:border-[#F7941D] bg-white" data-id="element-5105">
                                {APPROVERS.map(a => <option key={a.value} value={a.value} data-id="element-5106">
                                    {a.label}
                                  </option>)}
                              </select>
                              <ChevronDownIcon size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-5107" />
                            </div>
                          </td>
                        </tr>;
                })}
                  </tbody>
                </table>
              </div>
              <div className="px-6 py-4 bg-gray-50/50 border-t border-gray-100" data-id="element-5108">
                <p className="text-xs text-gray-500 mb-1" data-id="element-5109">
                  After confirming the payment method and optionally a
                  designated approver, your bills will be submitted for
                  approval.
                </p>
                <p className="text-xs text-gray-500" data-id="element-5110">
                  Your selected payment schedule is{' '}
                  <span className="font-semibold text-gray-700" data-id="element-5111">
                    {scheduleType === 'due_date' ? 'On the billing due date' : scheduleType === 'today' ? 'Immediately (today)' : 'On the next cycle'}
                  </span>
                </p>
              </div>
            </div>

            {/* Validation error */}
            {showValidationErrors && !allValid && <div className="text-center mb-6" data-id="element-5112">
                <p className="text-xs text-red-500 flex items-center justify-center gap-1.5" data-id="element-5113">
                  <AlertTriangleIcon size={14} data-id="element-5114" />
                  Unable to schedule payments. Please ensure all bills have a
                  payment method selected.
                </p>
              </div>}

            <div className="flex justify-center gap-4" data-id="element-5115">
              <button onClick={() => setStep(1)} className="px-8 py-2.5 text-sm font-medium text-[#F7941D] bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-id="element-5116">
                Back
              </button>
              <button onClick={handleConfirmSchedule} className={`px-8 py-2.5 text-sm font-medium rounded-lg transition-colors shadow-sm ${allValid ? 'bg-[#F7941D] hover:bg-[#D97B06] text-white' : 'bg-[#F7941D]/80 text-white hover:bg-[#D97B06]'}`} data-id="element-5117">
                Confirm and schedule
              </button>
            </div>
          </div>}

        {/* ========== STEP 3: Success ========== */}
        {step === 3 && <div className="animate-in fade-in slide-in-from-bottom-4 duration-300 max-w-2xl mx-auto text-center" data-id="element-5118">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-5119">
              <CheckCircleIcon size={32} className="text-green-600" data-id="element-5120" />
            </div>
            <h1 className="text-2xl font-semibold text-gray-900 mb-3" data-id="element-5121">
              Payments scheduled successfully
            </h1>
            <p className="text-sm text-gray-500 mb-10 max-w-md mx-auto" data-id="element-5122">
              {selectedBills.length} bill
              {selectedBills.length !== 1 ? 's have' : ' has'} been submitted
              for approval. Approvers will be notified to review and approve the
              scheduled payments.
            </p>

            {/* Summary cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10" data-id="element-5123">
              <div className="bg-white rounded-xl border border-gray-200 p-5 text-left" data-id="element-5124">
                <span className="text-xs text-gray-500 block mb-1" data-id="element-5125">
                  Total scheduled
                </span>
                <span className="text-lg font-bold text-gray-900" data-id="element-5126">
                  THB{' '}
                  {totalAmount.toLocaleString('en-US', {
                minimumFractionDigits: 2
              })}
                </span>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-5 text-left" data-id="element-5127">
                <span className="text-xs text-gray-500 block mb-1" data-id="element-5128">
                  Assigned to you
                </span>
                <span className="text-lg font-bold text-gray-900" data-id="element-5129">
                  {assignedToMe.length} bill
                  {assignedToMe.length !== 1 ? 's' : ''}
                </span>
                {assignedToMe.length > 0 && <p className="text-[10px] text-[#F7941D] mt-1 font-medium" data-id="element-5130">
                    View under "Assigned to me" tab
                  </p>}
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-5 text-left" data-id="element-5131">
                <span className="text-xs text-gray-500 block mb-1" data-id="element-5132">
                  Auto-assigned
                </span>
                <span className="text-lg font-bold text-gray-900" data-id="element-5133">
                  {autoAssigned.length} bill
                  {autoAssigned.length !== 1 ? 's' : ''}
                </span>
                <p className="text-[10px] text-gray-400 mt-1" data-id="element-5134">
                  Based on threshold rules
                </p>
              </div>
            </div>

            {/* Scheduled bills detail */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-10 text-left" data-id="element-5135">
              <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50" data-id="element-5136">
                <h3 className="text-sm font-semibold text-gray-900" data-id="element-5137">
                  Scheduled payments
                </h3>
              </div>
              <div className="divide-y divide-gray-100" data-id="element-5138">
                {selectedBills.map(bill => {
              const rowState = billRows.find(r => r.billId === bill.id);
              const paymentDate = getPaymentDate(scheduleType, bill.dueDate);
              const approverLabel = rowState?.designatedApprover === 'auto' ? 'Auto-assigned' : rowState?.designatedApprover || 'Auto-assigned';
              return <div key={bill.id} className="px-6 py-4 flex items-center justify-between" data-id="element-5139">
                      <div className="flex items-center gap-4 min-w-0" data-id="element-5140">
                        <div className="w-8 h-8 bg-green-50 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-5141">
                          <CheckCircleIcon size={16} className="text-green-500" data-id="element-5142" />
                        </div>
                        <div className="min-w-0" data-id="element-5143">
                          <div className="text-sm font-medium text-gray-900 truncate" data-id="element-5144">
                            {bill.supplierName}
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-5145">
                            {bill.billNumber} · Payment on{' '}
                            {formatDate(paymentDate)} · Approver:{' '}
                            {approverLabel}
                          </div>
                        </div>
                      </div>
                      <div className="text-sm font-semibold text-gray-900 flex-shrink-0 ml-4" data-id="element-5146">
                        {bill.currency}{' '}
                        {bill.amount.toLocaleString('en-US', {
                    minimumFractionDigits: 2
                  })}
                      </div>
                    </div>;
            })}
              </div>
            </div>

            <div className="flex justify-center gap-4" data-id="element-5147">
              <button onClick={() => navigate('/bills')} className="px-8 py-2.5 text-sm font-medium text-white bg-[#F7941D] rounded-lg hover:bg-[#D97B06] transition-colors shadow-sm" data-id="element-5148">
                Back to Bills
              </button>
            </div>
          </div>}
      </div>
    </div>;
}